import { useCallback, useEffect, useMemo, useState } from "react";
import { useAdminFetch } from "../lib/admin-api";
import { Button } from "../components/ui/Button";
import { useToast } from "../components/ui/Toast";

interface Tenant {
  tenant_id: string;
  slug: string;
  display_name: string;
  brand_id: string | null;
  onboarding_choice: string | null;
  status: string;
  created_at: string;
  user_count?: number | string;
}

interface TenantUser {
  user_id: string;
  email: string;
  role: string;
  invited_at: string;
  last_login_at: string | null;
}

interface InviteLink {
  user_id: string;
  email: string;
  link_url: string;
  expires_at: string;
}

const ROLE_COLORS: Record<string, string> = {
  owner: "bg-indigo-100 text-indigo-800 border-indigo-200",
  member: "bg-blue-100 text-blue-800 border-blue-200",
  viewer: "bg-gray-100 text-gray-700 border-gray-200",
};

function statusPill(status: string): string {
  return status === "suspended"
    ? "bg-red-100 text-red-800 border-red-200"
    : "bg-green-100 text-green-800 border-green-200";
}

function roleColor(role: string): string {
  return ROLE_COLORS[role] || "bg-gray-100 text-gray-700 border-gray-200";
}

function formatDate(iso: string | null, fallback = "—"): string {
  if (!iso) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  return new Intl.DateTimeFormat(undefined, { year: "numeric", month: "short", day: "numeric" }).format(d);
}

function formatRelative(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const day = 24 * 60 * 60 * 1000;
  const days = Math.floor(diffMs / day);
  if (days <= 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  return formatDate(iso);
}

export default function Tenants({ slug }: { slug?: string }) {
  const adminFetch = useAdminFetch();
  const toast = useToast();

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);

  const [newSlug, setNewSlug] = useState("");
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const [users, setUsers] = useState<TenantUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [toggling, setToggling] = useState(false);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("owner");
  const [inviting, setInviting] = useState(false);

  const [link, setLink] = useState<InviteLink | null>(null);

  const selected = useMemo(
    () => tenants.find((t) => t.slug === slug) ?? null,
    [tenants, slug]
  );

  const loadTenants = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminFetch<{ data: Tenant[] }>("/api/admin/tenants");
      setTenants(r.data || []);
    } catch (err) {
      toast.push(`Failed to load tenants: ${(err as Error).message}`, "error");
    } finally {
      setLoading(false);
    }
  }, [adminFetch, toast]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const loadUsers = useCallback(
    async (s: string) => {
      setUsersLoading(true);
      try {
        const r = await adminFetch<{ data: TenantUser[] }>(`/api/admin/tenants/${encodeURIComponent(s)}/users`);
        setUsers(r.data || []);
      } catch (err) {
        toast.push(`Failed to load users: ${(err as Error).message}`, "error");
        setUsers([]);
      } finally {
        setUsersLoading(false);
      }
    },
    [adminFetch, toast]
  );

  useEffect(() => {
    if (slug) loadUsers(slug);
    else setUsers([]);
  }, [slug, loadUsers]);

  const openTenant = (s: string) => {
    window.location.hash = `#/tenants/${encodeURIComponent(s)}`;
  };
  const closeDetail = () => {
    window.location.hash = "#/tenants";
  };

  const createTenant = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSlug.trim() || !newName.trim()) return;
    setCreating(true);
    try {
      await adminFetch("/api/admin/tenants", {
        method: "POST",
        body: JSON.stringify({ slug: newSlug.trim(), display_name: newName.trim() }),
      });
      toast.push("Tenant created", "success");
      setNewSlug("");
      setNewName("");
      await loadTenants();
    } catch (err) {
      toast.push(`Create failed: ${(err as Error).message}`, "error");
    } finally {
      setCreating(false);
    }
  };

  const toggleStatus = async () => {
    if (!selected) return;
    const next = selected.status === "suspended" ? "active" : "suspended";
    setToggling(true);
    try {
      await adminFetch(`/api/admin/tenants/${encodeURIComponent(selected.slug)}`, {
        method: "PATCH",
        body: JSON.stringify({ status: next }),
      });
      toast.push(next === "suspended" ? "Tenant suspended" : "Tenant reactivated", "success");
      await loadTenants();
    } catch (err) {
      toast.push(`Status change failed: ${(err as Error).message}`, "error");
    } finally {
      setToggling(false);
    }
  };

  const inviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected || !inviteEmail.trim()) return;
    setInviting(true);
    try {
      await adminFetch(`/api/admin/tenants/${encodeURIComponent(selected.slug)}/users`, {
        method: "POST",
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
      });
      toast.push("User invited", "success");
      setInviteEmail("");
      setInviteRole("owner");
      await Promise.all([loadUsers(selected.slug), loadTenants()]);
    } catch (err) {
      toast.push(`Invite failed: ${(err as Error).message}`, "error");
    } finally {
      setInviting(false);
    }
  };

  const generateLink = async (u: TenantUser) => {
    if (!selected) return;
    try {
      const r = await adminFetch<{ link_url: string; expires_at: string }>(
        `/api/admin/tenants/${encodeURIComponent(selected.slug)}/users/${encodeURIComponent(u.user_id)}/invite-link`,
        { method: "POST" }
      );
      setLink({ user_id: u.user_id, email: u.email, link_url: r.link_url, expires_at: r.expires_at });
    } catch (err) {
      toast.push(`Invite link failed: ${(err as Error).message}`, "error");
    }
  };

  const copyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link.link_url);
      toast.push("Copied to clipboard", "success");
    } catch {
      toast.push("Could not copy — select and copy manually", "error");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Tenants</h2>
          <p className="text-sm text-gray-600 mt-1">
            Brand accounts for the multi-tenant identity layer. Invite users and manage access.
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <form className="flex flex-wrap items-end gap-3" onSubmit={createTenant}>
          <label className="text-xs text-gray-500 flex flex-col gap-1">
            Slug
            <input
              type="text"
              value={newSlug}
              onChange={(e) => setNewSlug(e.target.value)}
              placeholder="raw_garden"
              className="text-sm border border-gray-200 rounded-md px-2 py-1 w-48"
            />
          </label>
          <label className="text-xs text-gray-500 flex flex-col gap-1">
            Display name
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Raw Garden"
              className="text-sm border border-gray-200 rounded-md px-2 py-1 w-56"
            />
          </label>
          <Button type="submit" variant="primary" disabled={creating || !newSlug.trim() || !newName.trim()}>
            {creating ? "Creating…" : "New tenant"}
          </Button>
        </form>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className={selected ? "lg:col-span-3" : "lg:col-span-5"}>
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
            {loading && tenants.length === 0 ? (
              <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
            ) : tenants.length === 0 ? (
              <div className="p-10 text-center text-sm text-gray-600">No tenants yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Tenant</th>
                    <th className="text-left px-4 py-2 font-medium">Status</th>
                    <th className="text-left px-4 py-2 font-medium">Users</th>
                    <th className="text-left px-4 py-2 font-medium">Onboarding</th>
                    <th className="text-left px-4 py-2 font-medium">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.map((t) => {
                    const active = selected?.slug === t.slug;
                    return (
                      <tr
                        key={t.tenant_id}
                        onClick={() => openTenant(t.slug)}
                        className={`border-t border-gray-100 cursor-pointer ${active ? "bg-accent-50" : "hover:bg-gray-50"}`}
                      >
                        <td className="px-4 py-2">
                          <div className="font-medium text-gray-900">{t.display_name}</div>
                          <div className="font-mono text-xs text-gray-500">{t.slug}</div>
                        </td>
                        <td className="px-4 py-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusPill(t.status)}`}>
                            {t.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-gray-700">{Number(t.user_count ?? 0)}</td>
                        <td className="px-4 py-2 text-gray-700">{t.onboarding_choice || "—"}</td>
                        <td className="px-4 py-2 text-gray-500 text-xs whitespace-nowrap" title={t.created_at}>
                          {formatRelative(t.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {selected && (
          <div className="lg:col-span-2">
            <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
              <div className="flex items-start justify-between gap-2 p-4 border-b border-gray-100">
                <div>
                  <div className="text-lg font-semibold text-gray-900">{selected.display_name}</div>
                  <div className="font-mono text-xs text-gray-500">{selected.slug}</div>
                  <span className={`mt-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${statusPill(selected.status)}`}>
                    {selected.status}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant={selected.status === "suspended" ? "primary" : "secondary"}
                    size="sm"
                    onClick={toggleStatus}
                    disabled={toggling}
                  >
                    {selected.status === "suspended" ? "Reactivate" : "Suspend"}
                  </Button>
                  <button
                    className="text-gray-400 hover:text-gray-700 text-lg leading-none px-1"
                    onClick={closeDetail}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div className="p-4">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Users</div>
                {usersLoading ? (
                  <div className="text-sm text-gray-500 py-4">Loading users…</div>
                ) : users.length === 0 ? (
                  <div className="text-sm text-gray-600 py-4">No users yet.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-wide text-gray-400">
                      <tr>
                        <th className="text-left py-1.5 font-medium">Email</th>
                        <th className="text-left py-1.5 font-medium">Role</th>
                        <th className="text-left py-1.5 font-medium">Last login</th>
                        <th className="py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.user_id} className="border-t border-gray-100 align-middle">
                          <td className="py-2 pr-2">
                            <div className="text-gray-900">{u.email}</div>
                            <div className="text-xs text-gray-400">invited {formatDate(u.invited_at)}</div>
                          </td>
                          <td className="py-2 pr-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${roleColor(u.role)}`}>
                              {u.role}
                            </span>
                          </td>
                          <td className="py-2 pr-2 text-xs text-gray-500">
                            {u.last_login_at ? formatDate(u.last_login_at) : "Never"}
                          </td>
                          <td className="py-2 text-right">
                            <Button variant="ghost" size="sm" onClick={() => generateLink(u)}>
                              Invite link
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                <form className="mt-4 border-t border-gray-100 pt-4 flex flex-wrap items-end gap-2" onSubmit={inviteUser}>
                  <label className="text-xs text-gray-500 flex flex-col gap-1 flex-1 min-w-[12rem]">
                    Invite user
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="person@brand.com"
                      className="text-sm border border-gray-200 rounded-md px-2 py-1"
                    />
                  </label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value)}
                    className="text-sm border border-gray-200 rounded-md px-2 py-1"
                  >
                    <option value="owner">owner</option>
                    <option value="member">member</option>
                    <option value="viewer">viewer</option>
                  </select>
                  <Button type="submit" variant="primary" size="sm" disabled={inviting || !inviteEmail.trim()}>
                    {inviting ? "Inviting…" : "Invite"}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>

      {link && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
          onClick={() => setLink(null)}
        >
          <div
            className="w-full max-w-lg rounded-lg border border-gray-200 bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Invite link</h3>
                <p className="text-sm text-gray-600">For {link.email}</p>
              </div>
              <button
                className="text-gray-400 hover:text-gray-700 text-lg leading-none px-1"
                onClick={() => setLink(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-2 text-xs font-mono text-gray-700 break-all">
              {link.link_url}
            </div>
            <p className="mt-2 text-xs text-gray-500">Expires {formatDate(link.expires_at)}</p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setLink(null)}>
                Close
              </Button>
              <Button variant="primary" onClick={copyLink}>
                Copy to clipboard
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
